import { usePageState } from "@/navigation/NavigationProvider";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Activity,
  Building2,
  Compass,
  HardDrive,
  History,
  KeyRound,
  LogOut,
  Mail,
  Plus,
  RefreshCw,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import DateTimePreferences from "@/components/DateTimePreferences";
import { formatTimestamp } from "@/utils/datetime";
import RuntimeHealthPanel from "@/components/RuntimeHealthPanel";
import FeatureMatrixPanel from "@/components/FeatureMatrixPanel";
import UserLimitsPanel from "@/components/UserLimitsPanel";
import PlatformAccessStudioPage from "@/pages/PlatformAccessStudioPage";
import PlatformGuardianPage from "@/pages/PlatformGuardianPage";
import PlatformSecurityPanel from "@/components/PlatformSecurityPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/platform`;

const apiErrorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => item?.msg)
      .filter((message) => typeof message === "string");
    return messages.join("; ") || fallback;
  }
  return typeof detail === "string" ? detail : fallback;
};

export default function PlatformCompaniesPage({ initialTab = "account-directory" }) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = usePageState("activeTab", initialTab);
  const [companies, setCompanies] = useState([]);
  const [overview, setOverview] = useState({
    total_companies: 0,
    active_companies: 0,
    active_owner_admins: 0,
  });
  const [form, setForm] = useState({
    name: "",
    display_name: "",
    owner_name: "",
    owner_email: "",
    owner_password: "",
  });
  const [emailEditor, setEmailEditor] = useState(null);
  const [correctedOwnerEmail, setCorrectedOwnerEmail] = useState("");
  const [emailEditorError, setEmailEditorError] = useState("");
  const [isSavingOwnerEmail, setIsSavingOwnerEmail] = useState(false);
  const [passwordEditor, setPasswordEditor] = useState(null);
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [temporaryPasswordConfirmation, setTemporaryPasswordConfirmation] = useState("");
  const [passwordEditorError, setPasswordEditorError] = useState("");
  const [isSavingTemporaryPassword, setIsSavingTemporaryPassword] = useState(false);
  const [auditCompany, setAuditCompany] = useState(null);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditError, setAuditError] = useState("");
  const [isAuditLoading, setIsAuditLoading] = useState(false);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState("");
  const [featureEditor, setFeatureEditor] = useState(null);
  const [featureFlags, setFeatureFlags] = useState(null);
  const [featureError, setFeatureError] = useState("");
  const [isFeatureLoading, setIsFeatureLoading] = useState(false);
  const [isSavingFeatures, setIsSavingFeatures] = useState(false);
  const [featureCatalog, setFeatureCatalog] = useState([]);

  // R2 storage health probe — click-to-run, session-authenticated. Never
  // displays or stores any secret material returned by the endpoint (the
  // backend already scrubs its own response).
  const [r2Report, setR2Report] = useState(null);
  const [r2Error, setR2Error] = useState("");
  const [isCheckingR2, setIsCheckingR2] = useState(false);

  // Scheduler drift badge — polls a lightweight endpoint every 60s so the
  // Runtime Health tab surfaces a subtle dot when any scheduler job goes
  // stale or degraded, without requiring the tab to be open.
  const [schedulerAlerts, setSchedulerAlerts] = useState(null);

  const loadDashboard = useCallback(async ({ notifyOnError = false } = {}) => {
    setIsDashboardLoading(true);
    setDashboardError("");
    try {
      const [companiesResponse, overviewResponse, catalogResponse] = await Promise.all([
        axios.get(`${API}/companies`),
        axios.get(`${API}/overview`),
        axios.get(`${API}/feature-catalog`),
      ]);
      setCompanies(companiesResponse.data);
      setOverview(overviewResponse.data);
      setFeatureCatalog(catalogResponse.data?.features || []);
    } catch (error) {
      const message = apiErrorMessage(
        error,
        "Platform account data could not be loaded.",
      );
      setDashboardError(message);
      if (notifyOnError) {
        toast.error(message);
      }
    } finally {
      setIsDashboardLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.platform_role === "platform_owner") loadDashboard();
  }, [loadDashboard, user?.platform_role]);

  useEffect(() => {
    if (user?.platform_role !== "platform_owner") return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/platform/production-health/scheduler-alerts`,
        );
        if (!cancelled) setSchedulerAlerts(response.data);
      } catch {
        if (!cancelled) setSchedulerAlerts(null);
      }
    };
    load();
    const interval = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user?.platform_role]);

  const createCompany = async (event) => {
    event.preventDefault();
    try {
      await axios.post(`${API}/companies`, form);
      setForm({
        name: "",
        display_name: "",
        owner_name: "",
        owner_email: "",
        owner_password: "",
      });
      await loadDashboard({ notifyOnError: true });
      toast.success("Company and Owner/Admin were created.");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Company could not be created."));
    }
  };

  const signOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const changeCompanyStatus = async (company) => {
    const action = company.status === "active" ? "deactivate" : "reactivate";
    const message = company.status === "active"
      ? `Deactivate ${company.name}? All company users will lose login and operational access.`
      : `Reactivate ${company.name}? Company users will regain access.`;
    if (!window.confirm(message)) return;
    try {
      await axios.post(`${API}/companies/${company.id}/${action}`);
      toast.success(`Company ${action}d.`);
      await loadDashboard({ notifyOnError: true });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Company lifecycle could not be updated."));
    }
  };

  const openOwnerEmailEditor = (company) => {
    setEmailEditor(company);
    setCorrectedOwnerEmail(company.owner_email || "");
    setEmailEditorError("");
  };

  const closeOwnerEmailEditor = () => {
    setEmailEditor(null);
    setCorrectedOwnerEmail("");
    setEmailEditorError("");
  };

  const openPasswordEditor = (company) => {
    setPasswordEditor(company);
    setTemporaryPassword("");
    setTemporaryPasswordConfirmation("");
    setPasswordEditorError("");
  };

  const closePasswordEditor = () => {
    setPasswordEditor(null);
    setTemporaryPassword("");
    setTemporaryPasswordConfirmation("");
    setPasswordEditorError("");
  };

  const openAuditTimeline = async (company) => {
    setAuditCompany(company);
    setAuditEntries([]);
    setAuditError("");
    setIsAuditLoading(true);
    try {
      const response = await axios.get(`${API}/companies/${company.id}/identity-audit`);
      setAuditEntries(response.data);
    } catch (error) {
      setAuditError(apiErrorMessage(error, "Identity history could not be loaded."));
    } finally {
      setIsAuditLoading(false);
    }
  };

  const openFeatureEditor = async (company) => {
    setFeatureEditor(company);
    setFeatureFlags(null);
    setFeatureError("");
    setIsFeatureLoading(true);
    try {
      const response = await axios.get(`${API}/companies/${company.id}/features`);
      setFeatureFlags(response.data);
    } catch (error) {
      setFeatureError(apiErrorMessage(error, "Feature settings could not be loaded."));
    } finally {
      setIsFeatureLoading(false);
    }
  };

  const closeFeatureEditor = () => {
    setFeatureEditor(null);
    setFeatureFlags(null);
    setFeatureError("");
  };

  const runR2HealthCheck = async () => {
    setIsCheckingR2(true);
    setR2Error("");
    setR2Report(null);
    try {
      const response = await axios.get(`${API}/r2-health`);
      setR2Report(response.data);
    } catch (error) {
      if (error?.response?.status === 404) {
        setR2Error(
          "This deployment does not include /api/platform/r2-health yet. Republish to pick up the latest build.",
        );
      } else {
        setR2Error(apiErrorMessage(error, "R2 health check failed."));
      }
    } finally {
      setIsCheckingR2(false);
    }
  };

  const toggleFeatureFlag = (key) => {
    setFeatureFlags((current) => (current ? { ...current, [key]: !current[key] } : current));
  };

  const saveFeatureFlags = async (event) => {
    event.preventDefault();
    if (!featureEditor || !featureFlags) return;
    setIsSavingFeatures(true);
    setFeatureError("");
    try {
      const response = await axios.patch(
        `${API}/companies/${featureEditor.id}/features`,
        featureFlags,
      );
      setFeatureFlags(response.data);
      toast.success(`${featureEditor.name} feature access updated.`);
      closeFeatureEditor();
    } catch (error) {
      const message = apiErrorMessage(error, "Feature access could not be updated.");
      setFeatureError(message);
      toast.error(message);
    } finally {
      setIsSavingFeatures(false);
    }
  };

  const saveOwnerEmail = async (event) => {
    event.preventDefault();
    if (!emailEditor) return;
    setEmailEditorError("");
    if (
      correctedOwnerEmail.trim().toLowerCase() === emailEditor.owner_email.toLowerCase()
    ) {
      const message = "The corrected email must differ from the current email.";
      setEmailEditorError(message);
      toast.error(message);
      return;
    }
    setIsSavingOwnerEmail(true);
    try {
      await axios.patch(`${API}/companies/${emailEditor.id}/owner-email`, {
        expected_owner_email: emailEditor.owner_email,
        owner_email: correctedOwnerEmail,
      });
      toast.success("Company Owner/Admin email updated.");
      closeOwnerEmailEditor();
      await loadDashboard({ notifyOnError: true });
    } catch (error) {
      const message = apiErrorMessage(error, "Owner email could not be updated.");
      setEmailEditorError(message);
      toast.error(message);
    } finally {
      setIsSavingOwnerEmail(false);
    }
  };

  const saveTemporaryPassword = async (event) => {
    event.preventDefault();
    if (!passwordEditor) return;
    setPasswordEditorError("");
    if (temporaryPassword !== temporaryPasswordConfirmation) {
      const message = "Temporary password confirmation does not match.";
      setPasswordEditorError(message);
      toast.error(message);
      return;
    }
    setIsSavingTemporaryPassword(true);
    try {
      await axios.patch(`${API}/companies/${passwordEditor.id}/owner-password`, {
        temporary_password: temporaryPassword,
      });
      toast.success("Temporary password set. Share it once through your secure channel.");
      closePasswordEditor();
    } catch (error) {
      const message = apiErrorMessage(
        error,
        "Temporary password could not be set.",
      );
      setPasswordEditorError(message);
      toast.error(message);
    } finally {
      setIsSavingTemporaryPassword(false);
    }
  };

  if (!user) {
    return (
      <main className="platform-page" data-testid="platform-owner-loading">
        <p>Loading Platform Owner access…</p>
      </main>
    );
  }

  return (
    <main className="platform-page" data-testid="platform-companies-page">
      <header className="platform-header">
        <div>
          <span className="operations-kicker">Platform administration</span>
          <h1>Platform Owner</h1>
          <p>Company account directory and Owner/Admin administration.</p>
        </div>
        <div className="platform-account-actions">
          <span data-testid="platform-owner-identity">
            {user?.email || user?.name} · Platform Owner
          </span>
          {user?.platform_role === "platform_owner" && (
            <button
              data-testid="platform-change-password-button"
              onClick={() => navigate("/change-password")}
              type="button"
            >
              <KeyRound size={15} />
              Change my password
            </button>
          )}
          <button
            data-testid="platform-refresh-button"
            onClick={() => loadDashboard({ notifyOnError: true })}
            type="button"
          >
            <RefreshCw size={15} />
            Refresh
          </button>
          <button
            data-testid="platform-logout-button"
            onClick={signOut}
            type="button"
          >
            <LogOut size={15} />
            Sign out
          </button>
          <DateTimePreferences />
        </div>
      </header>
      <nav
        aria-label="Platform Owner sections"
        className="platform-tab-bar"
        data-testid="platform-tab-bar"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.4rem",
          borderBottom: "1px solid #e2e5ea",
          margin: "0 0 1rem",
          padding: "0 0 0.4rem",
        }}
      >
        <button
          data-testid="platform-tab-account-directory"
          onClick={() => setActiveTab("account-directory")}
          style={{
            background: activeTab === "account-directory" ? "#111827" : "transparent",
            color: activeTab === "account-directory" ? "#fff" : "#374151",
            border: "1px solid",
            borderColor: activeTab === "account-directory" ? "#111827" : "#d1d5db",
            borderRadius: "999px",
            cursor: "pointer",
            fontSize: "0.85rem",
            fontWeight: 500,
            padding: "0.35rem 0.9rem",
          }}
          type="button"
        >
          <Building2 aria-hidden="true" size={14} style={{ marginRight: "0.35rem", verticalAlign: "-2px" }} />
          Account directory
        </button>
        <button
          data-testid="platform-tab-runtime-health"
          onClick={() => setActiveTab("runtime-health")}
          style={{
            background: activeTab === "runtime-health" ? "#111827" : "transparent",
            color: activeTab === "runtime-health" ? "#fff" : "#374151",
            border: "1px solid",
            borderColor: activeTab === "runtime-health" ? "#111827" : "#d1d5db",
            borderRadius: "999px",
            cursor: "pointer",
            fontSize: "0.85rem",
            fontWeight: 500,
            padding: "0.35rem 0.9rem",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.35rem",
          }}
          type="button"
        >
          <Activity aria-hidden="true" size={14} style={{ verticalAlign: "-2px" }} />
          Runtime Health
          {schedulerAlerts?.has_drift && (
            <span
              aria-label={`${schedulerAlerts.drift_count} scheduler job${
                schedulerAlerts.drift_count === 1 ? " needs" : "s need"
              } attention`}
              data-testid="platform-tab-runtime-health-drift-badge"
              style={{
                background: schedulerAlerts.stale_count > 0 ? "#b45309" : "#ca8a04",
                color: "#fff",
                borderRadius: "999px",
                fontSize: "0.7rem",
                fontWeight: 700,
                lineHeight: 1,
                minWidth: "1.2rem",
                padding: "0.15rem 0.4rem",
                textAlign: "center",
              }}
            >
              {schedulerAlerts.drift_count}
            </span>
          )}
        </button>
        <button
          data-testid="platform-tab-feature-matrix"
          onClick={() => setActiveTab("feature-matrix")}
          style={{
            background: activeTab === "feature-matrix" ? "#111827" : "transparent",
            color: activeTab === "feature-matrix" ? "#fff" : "#374151",
            border: "1px solid",
            borderColor: activeTab === "feature-matrix" ? "#111827" : "#d1d5db",
            borderRadius: "999px",
            cursor: "pointer",
            fontSize: "0.85rem",
            fontWeight: 500,
            padding: "0.35rem 0.9rem",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.35rem",
          }}
          type="button"
        >
          <Settings2 aria-hidden="true" size={14} style={{ verticalAlign: "-2px" }} />
          Feature matrix
        </button>
        <button
          data-testid="platform-tab-user-limits"
          onClick={() => setActiveTab("user-limits")}
          style={{
            background: activeTab === "user-limits" ? "#111827" : "transparent",
            color: activeTab === "user-limits" ? "#fff" : "#374151",
            border: "1px solid",
            borderColor: activeTab === "user-limits" ? "#111827" : "#d1d5db",
            borderRadius: "999px",
            cursor: "pointer",
            fontSize: "0.85rem",
            fontWeight: 500,
            padding: "0.35rem 0.9rem",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.35rem",
          }}
          type="button"
        >
          <Users aria-hidden="true" size={14} style={{ verticalAlign: "-2px" }} />
          User limits
        </button>
        <button
          data-testid="platform-tab-security"
          onClick={() => setActiveTab("security")}
          style={{
            background: activeTab === "security" ? "#111827" : "transparent",
            color: activeTab === "security" ? "#fff" : "#374151",
            border: "1px solid",
            borderColor: activeTab === "security" ? "#111827" : "#d1d5db",
            borderRadius: "999px",
            cursor: "pointer",
            fontSize: "0.85rem",
            fontWeight: 500,
            padding: "0.35rem 0.9rem",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.35rem",
          }}
          type="button"
        >
          <ShieldCheck aria-hidden="true" size={14} style={{ verticalAlign: "-2px" }} />
          Security
        </button>
        <button
          data-testid="platform-tab-access-studio"
          onClick={() => setActiveTab("access-studio")}
          style={{
            background: activeTab === "access-studio" ? "#111827" : "transparent",
            color: activeTab === "access-studio" ? "#fff" : "#374151",
            border: "1px solid",
            borderColor: activeTab === "access-studio" ? "#111827" : "#d1d5db",
            borderRadius: "999px",
            cursor: "pointer",
            fontSize: "0.85rem",
            fontWeight: 500,
            padding: "0.35rem 0.9rem",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.35rem",
          }}
          type="button"
        >
          <Compass aria-hidden="true" size={14} style={{ verticalAlign: "-2px" }} />
          Access Studio
        </button>
        <button
          data-testid="platform-tab-guardian"
          onClick={() => setActiveTab("guardian")}
          aria-pressed={activeTab === "guardian"}
          style={{ background: activeTab === "guardian" ? "#111827" : "transparent", color: activeTab === "guardian" ? "#fff" : "#374151", border: "1px solid #d1d5db", borderRadius: "999px", cursor: "pointer", fontSize: "0.85rem", fontWeight: 500, padding: "0.35rem 0.9rem" }}
          type="button"
        >Guardian</button>
      </nav>
      {activeTab === "guardian" && <PlatformGuardianPage />}
      {activeTab === "runtime-health" && <RuntimeHealthPanel />}
      {activeTab === "feature-matrix" && <FeatureMatrixPanel onOpenEditor={openFeatureEditor} />}
      {activeTab === "user-limits" && <UserLimitsPanel />}
      {activeTab === "access-studio" && <PlatformAccessStudioPage />}
      {activeTab === "security" && <PlatformSecurityPanel />}
      {activeTab === "account-directory" && (
        <>
      {dashboardError && (
        <section
          className="platform-dashboard-error"
          data-testid="platform-dashboard-error"
          role="alert"
        >
          {dashboardError}
        </section>
      )}
      <section
        className="platform-owner-email-editor"
        data-testid="platform-r2-health-panel"
        style={{ marginBottom: "1rem" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.35rem" }}>
          <HardDrive aria-hidden="true" size={18} />
          <h3 style={{ margin: 0 }}>R2 storage health</h3>
        </div>
        <p className="platform-owner-email-warning">
          Read-only probe against Cloudflare R2. Uses HEAD requests only — never lists,
          reads, writes, or deletes objects, and never exposes secret values.
        </p>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <button
            data-testid="platform-r2-health-check"
            disabled={isCheckingR2}
            onClick={runR2HealthCheck}
            type="button"
          >
            {isCheckingR2 ? "Checking…" : "Run R2 check"}
          </button>
          {r2Report && (
            <strong
              data-testid="platform-r2-health-verdict"
              style={{ color: r2Report.ready ? "#166534" : "#b45309" }}
            >
              {r2Report.ready ? "✓ R2 ready" : "⚠ Review checks"}
            </strong>
          )}
        </div>
        {r2Error && (
          <p data-testid="platform-r2-health-error" role="alert" style={{ marginTop: "0.5rem" }}>
            {r2Error}
          </p>
        )}
        {r2Report && (
          <pre
            data-testid="platform-r2-health-report"
            style={{
              background: "#f6f7f9",
              border: "1px solid #e2e5ea",
              borderRadius: "6px",
              fontSize: "0.78rem",
              margin: "0.6rem 0 0",
              maxHeight: "260px",
              overflow: "auto",
              padding: "0.6rem 0.75rem",
              whiteSpace: "pre-wrap",
            }}
          >
            {JSON.stringify(r2Report, null, 2)}
          </pre>
        )}
      </section>
      <section
        className="platform-access-boundary"
        data-testid="platform-access-boundary-alert"
      >
        <div className="platform-access-boundary-heading">
          <ShieldAlert aria-hidden="true" size={22} />
          <div>
            <span className="operations-kicker">Permission boundary</span>
            <h2>Platform access: identity and company accounts only</h2>
            <p>
              Your platform role manages company setup and Owner/Admin account identity.
              Tenant operations remain isolated inside each company workspace.
            </p>
          </div>
        </div>
        <div className="platform-access-rules">
          <div data-testid="platform-allowed-access">
            <strong>You can manage</strong>
            <ul>
              <li>Create company accounts and their initial Owner/Admin</li>
              <li>Review company name, account status, and Owner/Admin contact details</li>
              <li>Correct an Owner/Admin sign-in email and issue a recovery password</li>
            </ul>
          </div>
          <div data-testid="platform-blocked-access">
            <strong>Not available at platform level</strong>
            <ul>
              <li>Projects, permits, locates, invoices, payroll, and communications</li>
              <li>Company operational records or field activity</li>
              <li>Company user passwords, operational records, or session data</li>
            </ul>
          </div>
        </div>
      </section>
      <section className="platform-overview" data-testid="platform-overview">
        <article data-testid="platform-total-companies">
          <Building2 size={18} />
          <span>Total companies</span>
          <strong>{overview.total_companies}</strong>
        </article>
        <article data-testid="platform-active-companies">
          <ShieldCheck size={18} />
          <span>Active companies</span>
          <strong>{overview.active_companies}</strong>
        </article>
        <article data-testid="platform-active-owner-admins">
          <Users size={18} />
          <span>Active Owner/Admins</span>
          <strong>{overview.active_owner_admins}</strong>
        </article>
      </section>
      <section className="platform-company-grid">
        <article
          className="platform-company-list"
          data-testid="platform-companies-list"
        >
          <h2>
            <Building2 size={18} />
            Company directory
          </h2>
          <p className="platform-directory-note">
            Identity-only directory. Operational company records are never displayed here.
          </p>
          {isDashboardLoading && (
            <p data-testid="platform-dashboard-loading" className="platform-directory-note">
              Refreshing company account directory…
            </p>
          )}
          <div className="platform-directory-columns" data-testid="platform-directory-columns">
            <span>Company</span>
            <span>Owner/Admin email</span>
            <span>Status</span>
            <span>Identity action</span>
          </div>
          <h3 data-testid="platform-active-companies-heading">Active Companies</h3>
          {companies.filter((company) => company.status === "active").map((company) => (
            <div
              className="platform-company-row"
              data-testid={`platform-company-${company.id}`}
              key={company.id}
            >
              <div>
                <strong>{company.name}</strong>
                <small data-testid={`platform-company-id-${company.id}`}>{company.id}</small>
              </div>
              <span>{company.owner_email}</span>
              <small>{company.status}</small>
              <div className="platform-company-actions">
                <button
                  data-testid={`platform-company-${company.id}-edit-owner-email-button`}
                  onClick={() => openOwnerEmailEditor(company)}
                  type="button"
                >
                  <Mail size={14} />
                  Correct owner email
                </button>
                <button
                  data-testid={`platform-company-${company.id}-set-temporary-password-button`}
                  onClick={() => openPasswordEditor(company)}
                  type="button"
                >
                  <KeyRound size={14} />
                  Set temporary password
                </button>
                <button
                  data-testid={`platform-company-${company.id}-identity-audit-button`}
                  onClick={() => openAuditTimeline(company)}
                  type="button"
                >
                  <History size={14} />
                  Identity history
                </button>
                <button
                  data-testid={`platform-company-${company.id}-feature-access-button`}
                  onClick={() => openFeatureEditor(company)}
                  type="button"
                >
                  <Settings2 size={14} />
                  Feature access
                </button>
                <button
                  data-testid={`platform-company-${company.id}-deactivate-button`}
                  onClick={() => changeCompanyStatus(company)}
                  type="button"
                >
                  Deactivate company
                </button>
              </div>
            </div>
          ))}
          <h3
            className="platform-deactivated-heading"
            data-testid="platform-deactivated-companies-heading"
          >
            Deactivated Companies
          </h3>
          {companies.filter((company) => company.status === "deactivated").map((company) => (
            <div
              className="platform-company-row platform-company-row-deactivated"
              data-testid={`platform-deactivated-company-${company.id}`}
              key={company.id}
            >
              <div>
                <strong>{company.name}</strong>
                <small>{company.id}</small>
              </div>
              <span>{company.owner_email}</span>
              <small>deactivated</small>
              <div className="platform-company-actions">
                <button
                  data-testid={`platform-company-${company.id}-reactivate-button`}
                  onClick={() => changeCompanyStatus(company)}
                  type="button"
                >
                  Reactivate company
                </button>
              </div>
            </div>
          ))}
          {emailEditor && (
            <form
              className="platform-owner-email-editor"
              data-testid="platform-owner-email-editor"
              onSubmit={saveOwnerEmail}
            >
              <h3>Correct {emailEditor.name} Owner/Admin email</h3>
              <p
                className="platform-owner-email-warning"
                data-testid="platform-owner-email-warning"
              >
                This updates the Owner/Admin sign-in email only. Their password, company role,
                and active session remain unchanged.
              </p>
              <label>
                Current owner email
                <input
                  data-testid="platform-current-owner-email-input"
                  readOnly
                  value={emailEditor.owner_email}
                />
              </label>
              <label>
                Corrected owner email
                <input
                  data-testid="platform-corrected-owner-email-input"
                  onChange={(event) => setCorrectedOwnerEmail(event.target.value)}
                  required
                  type="email"
                  value={correctedOwnerEmail}
                />
              </label>
              {emailEditorError && (
                <p data-testid="platform-owner-email-editor-error" role="alert">
                  {emailEditorError}
                </p>
              )}
              <div className="platform-owner-email-editor-actions">
                <button
                  data-testid="platform-save-owner-email-button"
                  disabled={isSavingOwnerEmail}
                  type="submit"
                >
                  {isSavingOwnerEmail ? "Saving…" : "Save owner email"}
                </button>
                <button
                  data-testid="platform-cancel-owner-email-button"
                  disabled={isSavingOwnerEmail}
                  onClick={closeOwnerEmailEditor}
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
          {passwordEditor && (
            <form
              className="platform-owner-password-editor"
              data-testid="platform-owner-password-editor"
              onSubmit={saveTemporaryPassword}
            >
              <h3>Set {passwordEditor.name} Owner/Admin temporary password</h3>
              <p data-testid="platform-owner-password-warning">
                Share this password through your secure channel. It is never shown again after
                saving, and the Owner/Admin must replace it after sign-in.
              </p>
              <label>
                Temporary password
                <input
                  data-testid="platform-temporary-password-input"
                  minLength="12"
                  onChange={(event) => setTemporaryPassword(event.target.value)}
                  required
                  type="password"
                  value={temporaryPassword}
                />
              </label>
              <label>
                Confirm temporary password
                <input
                  data-testid="platform-temporary-password-confirmation-input"
                  minLength="12"
                  onChange={(event) => setTemporaryPasswordConfirmation(event.target.value)}
                  required
                  type="password"
                  value={temporaryPasswordConfirmation}
                />
              </label>
              {passwordEditorError && (
                <p data-testid="platform-owner-password-editor-error" role="alert">
                  {passwordEditorError}
                </p>
              )}
              <div className="platform-owner-email-editor-actions">
                <button
                  data-testid="platform-save-temporary-password-button"
                  disabled={isSavingTemporaryPassword}
                  type="submit"
                >
                  {isSavingTemporaryPassword ? "Saving…" : "Set temporary password"}
                </button>
                <button
                  data-testid="platform-cancel-temporary-password-button"
                  disabled={isSavingTemporaryPassword}
                  onClick={closePasswordEditor}
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
          {featureEditor && (
            <form
              className="platform-owner-email-editor"
              data-testid="platform-feature-access-editor"
              onSubmit={saveFeatureFlags}
            >
              <h3>{featureEditor.name} feature access</h3>
              <p className="platform-owner-email-warning">
                Disabling a module hides it from this company's UI and blocks the backend
                APIs immediately. Existing data is preserved and comes back the moment the
                module is re-enabled. Turning Mobile off signs out mobile devices but leaves
                web sessions alone.
              </p>
              {isFeatureLoading && (
                <p data-testid="platform-feature-access-loading">Loading feature access…</p>
              )}
              {featureError && (
                <p data-testid="platform-feature-access-error" role="alert">
                  {featureError}
                </p>
              )}
              {featureFlags && (
                <div className="platform-feature-toggles" data-testid="platform-feature-toggles">
                  {featureCatalog.map(({ key, label }) => (
                    <label
                      className="platform-feature-toggle"
                      data-testid={`platform-feature-toggle-${key}`}
                      key={key}
                    >
                      <input
                        checked={Boolean(featureFlags[key])}
                        data-testid={`platform-feature-toggle-${key}-input`}
                        onChange={() => toggleFeatureFlag(key)}
                        type="checkbox"
                      />
                      <span>
                        <strong>{label}</strong>
                        <small>{featureFlags[key] ? "Enabled" : "Disabled"}</small>
                      </span>
                    </label>
                  ))}
                </div>
              )}
              <div className="platform-owner-email-editor-actions">
                <button
                  data-testid="platform-save-feature-access-button"
                  disabled={isSavingFeatures || !featureFlags}
                  type="submit"
                >
                  {isSavingFeatures ? "Saving…" : "Save feature access"}
                </button>
                <button
                  data-testid="platform-cancel-feature-access-button"
                  disabled={isSavingFeatures}
                  onClick={closeFeatureEditor}
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
          {auditCompany && (
            <section
              className="platform-identity-audit"
              data-testid="platform-identity-audit-timeline"
            >
              <div className="platform-identity-audit-header">
                <div>
                  <span className="operations-kicker">Identity audit</span>
                  <h3>{auditCompany.name} Owner/Admin history</h3>
                </div>
                <button
                  data-testid="platform-close-identity-audit-button"
                  onClick={() => setAuditCompany(null)}
                  type="button"
                >
                  Close
                </button>
              </div>
              <p>
                This records identity actions only. Temporary passwords and hashes are never shown.
              </p>
              {isAuditLoading && (
                <p data-testid="platform-identity-audit-loading">Loading history…</p>
              )}
              {auditError && (
                <p data-testid="platform-identity-audit-error" role="alert">
                  {auditError}
                </p>
              )}
              {!isAuditLoading && !auditError && !auditEntries.length && (
                <p data-testid="platform-identity-audit-empty">No identity actions recorded yet.</p>
              )}
              <ol>
                {auditEntries.map((entry) => (
                  <li
                    data-testid={`platform-identity-audit-entry-${entry.id}`}
                    key={entry.id}
                  >
                    <strong>
                      {entry.action === "company_owner_email_corrected"
                        ? "Owner/Admin email corrected"
                        : "Temporary password issued"}
                    </strong>
                    <span>{formatTimestamp(entry.occurred_at)}</span>
                    <small>By {entry.actor_name_snapshot}</small>
                    {entry.action === "company_owner_email_corrected" && (
                      <em>
                        {entry.previous_company_owner_email || entry.previous_user_email}
                        {" → "}
                        {entry.corrected_owner_email}
                      </em>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          )}
        </article>
        <article className="platform-company-form">
          <h2>
            <Plus size={18} />
            Create company account
          </h2>
          <p className="platform-directory-note">
            Creates an isolated company workspace and its separate Owner/Admin account. Share
            the initial password once; it is never displayed again after creation.
          </p>
          <form data-testid="platform-company-form" onSubmit={createCompany}>
            <label>
              Company name
              <input
                data-testid="platform-company-name-input"
                onChange={(event) =>
                  setForm({ ...form, name: event.target.value })
                }
                required
                value={form.name}
              />
            </label>
            <label>
              Display name
              <input
                data-testid="platform-company-display-name-input"
                onChange={(event) =>
                  setForm({ ...form, display_name: event.target.value })
                }
                value={form.display_name}
              />
            </label>
            <label>
              Owner name
              <input
                data-testid="platform-owner-name-input"
                onChange={(event) =>
                  setForm({ ...form, owner_name: event.target.value })
                }
                required
                value={form.owner_name}
              />
            </label>
            <label>
              Owner email
              <input
                data-testid="platform-owner-email-input"
                onChange={(event) =>
                  setForm({ ...form, owner_email: event.target.value })
                }
                required
                type="email"
                value={form.owner_email}
              />
            </label>
            <label>
              Initial temporary password
              <input
                data-testid="platform-owner-password-input"
                minLength="12"
                onChange={(event) =>
                  setForm({ ...form, owner_password: event.target.value })
                }
                required
                type="password"
                value={form.owner_password}
              />
            </label>
            <button data-testid="platform-create-company-submit" type="submit">
              Create company
            </button>
          </form>
        </article>
      </section>
        </>
      )}

    </main>
  );
}



